import { http } from "./client";

// Employees: list, counts, profile record, photo and bulk import.
// Each function returns the response body (same data the old Redux thunks returned).
const get = (url, config) => http.get(url, config).then((r) => r.data);
const multipart = { headers: { "Content-Type": "multipart/form-data" } };

const isSet = (v) => v && v !== "All";

export const employeeApi = {
  /**
   * Paginated employee list → { data, meta }.
   * filters: companyId, branchId, roleId, page, limit, search, department, status, assignmentType
   */
  getList: (filters = {}) => {
    const {
      companyId,
      branchId,
      roleId,
      page,
      limit,
      search,
      department,
      status,
      assignmentType,
      hiredFrom,
    } = filters;
    const params = { page: page || 1, limit: limit || 10 };
    // Hired from that date (YYYY-MM-DD) up to today
    if (isSet(hiredFrom)) params.hiredFrom = hiredFrom;
    if (isSet(branchId)) params.branchId = branchId;
    if (isSet(roleId)) params.roleId = roleId;
    if (search && search.trim()) {
      params.search = search.trim();
      params.name = search.trim();
    }
    if (department && department.trim()) params.department = department.trim();
    if (isSet(status)) params.status = status;
    if (
      assignmentType &&
      assignmentType.toLowerCase() !== "both" &&
      assignmentType !== "All"
    ) {
      params.assignmentType = assignmentType;
    }
    // by-company serves COMPANY scope (whole company) and BRANCH scope (managed branch trees)
    const url = companyId ? `/employee/by-company/${companyId}` : "/employee";
    return get(url, { params });
  },

  /** Employee count for a company → number */
  getCount: (filters = {}) => {
    const { companyId, branchId, roleId, status, assignmentType } = filters;
    const params = {};
    if (isSet(branchId)) params.branchId = branchId;
    if (isSet(roleId)) params.roleId = roleId;
    if (isSet(status)) params.status = status;
    if (
      assignmentType &&
      assignmentType.toLowerCase() !== "both" &&
      assignmentType !== "All"
    ) {
      params.assignmentType = assignmentType;
    }
    return get(`/employee/by-company/${companyId}/count`, { params }).then(
      (res) => (typeof res === "object" && res !== null ? res.count : res),
    );
  },

  getById: (id) => get(`/employee/${id}`),
  create: (data) => http.post("/employee", data).then((r) => r.data),
  update: (id, data) => http.put(`/employee/${id}`, data).then((r) => r.data),

  /** Recalculates the employee's status (Complete/Incomplete) on the server. */
  checkCompleteness: (id) =>
    http.patch(`/employee/${id}/check-completeness`).then((r) => r.data),

  /* ---------- Photo ---------- */
  getImage: (employeeId) => get(`/employee-image/${employeeId}`),
  uploadImage: (employeeId, file) => {
    const payload =
      file instanceof FormData
        ? file
        : (() => {
            const fd = new FormData();
            fd.append("image", file);
            return fd;
          })();
    return http
      .post(`/employee-image/${employeeId}/upload`, payload, multipart)
      .then((r) => r.data);
  },

  /* ---------- Bulk import (runs in the background on the server) ---------- */

  /**
   * POST /employee/import → { status: "PROCESSING", batchId, totalRows }
   * Returns immediately; rows are processed in the background.
   * No Content-Type header: the browser sets multipart/form-data with the boundary.
   */
  startImport: ({ file, companyId }) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("companyId", companyId);
    return http.post("/employee/import", formData).then((r) => r.data);
  },

  /**
   * GET /employee/import/:batchId
   * → { status: "PROCESSING" | "COMPLETED" | "FAILED", batchId, fileName, stats, results }
   * `results` stays empty until the import finishes.
   */
  getImportStatus: (batchId) => get(`/employee/import/${batchId}`),
};
